import { cn } from "@ando/ui";
import { useLingui } from "@lingui/react/macro";
import { useNavigate } from "@tanstack/react-router";
import { Check, ChevronRight, FileText, LoaderCircle } from "lucide-react";
import * as React from "react";
import { useShallow } from "zustand/shallow";

import { getWorkspaceTabDef } from "@/features/workspace/config";
import type { ActionDrawerStatus } from "@ando/ui";
import { resolveTabForActionType } from "../actionTypeToTab";
import {
  type ActiveAction,
  selectActionsByQuery,
  useActiveActionStore,
} from "../state/activeActionStore";
import { iconForActionType } from "../utils/actionTypeIcon";
import styles from "./MessageActionChips.module.css";

/**
 * MessageActionChips — inline strip mounted under each agent message
 * that links back to any action proposal birthed by the same chat turn.
 *
 * Mirrors the ``SourceRailSurface`` chip pattern but for HITL proposals
 * (e.g. ``document_create`` materialises a new file, the user later
 * wants to re-open it from history — explicit ask May 24, 2026: "come
 * capisco quale doc è stato creato con quella domanda? E come ci
 * riarrivo?"). Without this chip, the only way back is the persistent
 * notification chip in ``ActionFormSurface`` which lives at the top of
 * the work area, NOT below the message that birthed it — once dismissed
 * the link is gone.
 *
 * Click → ``navigate({ search: { proposal: <id> } })`` on the action's
 * destination tab → re-opens the ``FocusedActionView`` with the same
 * preview + Conferma/Rifiuta state machine. Works for pending AND
 * executed proposals; the action survives in the store until the user
 * explicitly dismisses it.
 *
 * Returns ``null`` when there are no actions tied to this query — most
 * agent answers are pure Q&A with citations only.
 *
 * Document actions are presented as file rows: readable name, lifecycle
 * status and a verb describing navigation. Opening never confirms an action.
 */
export interface MessageActionChipsProps {
  readonly queryId: string | null;
}

export const MessageActionChips: React.FC<MessageActionChipsProps> = ({ queryId }) => {
  // `useShallow` so the surface doesn't re-render on every store mutation
  // — only when this specific message's action list changes (May 24,
  // 2026 — same pattern as ``useActiveActionsForTab`` to dodge React
  // #185 in the SyncExternalStore flow).
  // Memo the curried selector so we don't alloc a new closure per render
  // (audit fix N1 May 25, 2026). ``useShallow`` already short-circuits
  // on identical results, but cheaper to skip the function alloc too.
  const selector = React.useMemo(() => selectActionsByQuery(queryId), [queryId]);
  const actions = useActiveActionStore(useShallow(selector));
  const navigate = useNavigate();

  if (actions.length === 0) return null;

  return (
    <div className={cn(styles.list, "mt-3 flex w-full max-w-lg flex-col gap-2")}>
      {actions.map((a) => (
        <ActionChip key={a.id} action={a} onOpen={navigate} />
      ))}
    </div>
  );
};
MessageActionChips.displayName = "MessageActionChips";

const ActionChip: React.FC<{
  readonly action: ActiveAction;
  readonly onOpen: ReturnType<typeof useNavigate>;
}> = ({ action, onOpen }) => {
  const { t } = useLingui();
  const Icon = iconForActionType(action.actionType);
  const onClick = React.useCallback(() => {
    const tab = resolveTabForActionType(action.actionType);
    const def = getWorkspaceTabDef(tab);
    if (!def) return;
    // Document edit/fork chips open the EDITOR (``?focus=<docId>``) — that's
    // where the inline redline + "Accetta tutte" live — NOT the scalar proposal
    // drawer (``?proposal=``), which renders nothing for these (Jun 22, 2026:
    // "clicco il chip e non apre nulla"). Routing to the SAME ``documentId`` the
    // proposal/diff used means ``pendingEditAction`` re-matches on re-open, so
    // the persisted redline re-appears instead of the clean doc.
    const resultDocId = action.executedData?.doc_id ?? action.executedData?.document_id;
    if (action.status === "executed" && typeof resultDocId === "string") {
      void onOpen({ to: def.route, search: { focus: resultDocId } });
      return;
    }
    const docId = action.documentEdit?.documentId;
    if (
      docId &&
      (action.actionType === "document_edit" || action.actionType === "document_fork_edit")
    ) {
      void onOpen({ to: def.route, search: { focus: docId } });
      return;
    }
    void onOpen({ to: def.route, search: { proposal: action.id } });
  }, [
    action.actionType,
    action.id,
    action.documentEdit?.documentId,
    action.executedData,
    action.status,
    onOpen,
  ]);

  const isDocument = action.actionType.startsWith("document_");
  const filename =
    action.documentEdit?.documentTitle ?? action.documentCreate?.title ?? action.title;
  const extension = isDocument
    ? filename.match(/\.([a-z0-9]{2,5})$/i)?.[1]?.toUpperCase()
    : undefined;
  const name = isDocument
    ? filename.replace(/\.[a-z0-9]{2,5}$/i, "").replace(/_/g, " ")
    : action.title;
  const isSaving = action.status === "confirming" || action.status === "executing";
  const isSaved = action.status === "executed";
  const statusText: Record<ActionDrawerStatus, string> = {
    proposed:
      action.actionType === "document_create"
        ? t`Documento da rivedere`
        : isDocument
          ? t`Modifiche da rivedere`
          : t`Da rivedere`,
    editing: t`Revisione in corso`,
    confirming: isDocument ? t`Salvataggio in corso` : t`Operazione in corso`,
    executing: isDocument ? t`Salvataggio in corso` : t`Operazione in corso`,
    executed: isDocument ? t`Documento salvato` : t`Azione completata`,
    error: t`Operazione non completata`,
  };
  const openLabel = isSaved
    ? isDocument
      ? t`Apri documento`
      : t`Apri dettagli`
    : action.actionType === "document_create"
      ? t`Apri anteprima`
      : isDocument && action.status !== "error"
        ? t`Rivedi modifiche`
        : t`Rivedi proposta`;
  const ariaLabel = `${openLabel}: ${filename}`;

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel}
      title={filename}
      className={cn(
        styles.row,
        "group grid w-full min-h-[64px] items-center gap-x-3 gap-y-2 rounded-xl border border-hairline bg-white/70 px-3 py-2 text-left",
        "transition-colors duration-150 motion-reduce:transition-none hover:border-ando-orange/35 hover:bg-white active:bg-cream-deep",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/60 focus-visible:ring-offset-2",
      )}
    >
      <span
        aria-hidden
        className="flex h-10 w-9 shrink-0 flex-col items-center justify-center gap-1 rounded-md border border-hairline bg-cream text-ink-2"
      >
        {isDocument ? (
          <FileText size={17} strokeWidth={1.5} />
        ) : (
          <Icon size={18} strokeWidth={1.5} />
        )}
        {extension ? (
          <span className="text-[8px] font-semibold leading-none tracking-[0.03em]">
            {extension}
          </span>
        ) : null}
      </span>
      <span className="min-w-0">
        <span className="block text-[13px] font-medium leading-snug text-ink-1 [overflow-wrap:anywhere]">
          {name}
        </span>
        <span
          className={cn(
            "mt-0.5 flex items-center gap-1.5 text-[11px] leading-snug",
            action.status === "error" ? "text-error" : "text-ink-3",
          )}
        >
          {isSaved ? (
            <Check size={12} strokeWidth={2} aria-hidden />
          ) : isSaving ? (
            <LoaderCircle
              size={12}
              className="animate-spin motion-reduce:animate-none"
              aria-hidden
            />
          ) : (
            <span
              className={cn(
                "h-1.5 w-1.5 shrink-0 rounded-full",
                action.status === "error" ? "bg-error" : "bg-ando-orange",
              )}
              aria-hidden
            />
          )}
          <span aria-live="polite">{statusText[action.status]}</span>
        </span>
      </span>
      <span
        className={cn(
          styles.open,
          "flex items-center gap-1 self-center text-[12px] font-semibold text-ink-2 group-hover:text-ink-1",
        )}
      >
        {openLabel}
        <ChevronRight
          size={14}
          strokeWidth={1.5}
          aria-hidden
          className="text-ink-3 transition-transform duration-150 group-hover:translate-x-0.5 motion-reduce:transform-none motion-reduce:transition-none"
        />
      </span>
    </button>
  );
};
ActionChip.displayName = "ActionChip";
